import { useMemo } from 'react';
import { Users, Trophy, Zap, ClipboardCheck, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { TeamMember } from '@/hooks/useCoach';
import type { AssignmentCompletion } from '@/hooks/useAssignmentCompletion';


export type HealthStatus = 'active' | 'at-risk' | 'inactive';

interface TeamDashboardCardProps {
  members: TeamMember[];
  healthFilter: HealthStatus | null;
  onHealthFilterChange: (next: HealthStatus | null) => void;
  completion: AssignmentCompletion;
  completionError?: string | null;
}

export function getHealthStatus(lastActiveDate?: string): HealthStatus {
  if (!lastActiveDate) return 'inactive';
  const days = (Date.now() - new Date(lastActiveDate).getTime()) / 86400000;
  if (days <= 7) return 'active';
  if (days <= 14) return 'at-risk';
  return 'inactive';
}

export const HEALTH_RING: Record<HealthStatus, string> = {
  active: 'ring-emerald-400/70',
  'at-risk': 'ring-amber-400/70',
  inactive: 'ring-rose-400/60',
};

export default function TeamDashboardCard({
  members,
  healthFilter,
  onHealthFilterChange,
  completion,
  completionError,
}: TeamDashboardCardProps) {

  const { activeThisWeek, activeLastWeek, avgGameIQ, totalXP, healthCounts } = useMemo(() => {
    const now = Date.now();
    const day = 86400000;
    const active7 = members.filter(m => m.last_active_date && (now - new Date(m.last_active_date).getTime()) / day <= 7).length;
    const active7to14 = members.filter(m => {
      if (!m.last_active_date) return false;
      const d = (now - new Date(m.last_active_date).getTime()) / day;
      return d > 7 && d <= 14;
    }).length;

    const avg = members.length > 0
      ? Math.round(members.reduce((s, m) => s + (m.level || 0), 0) / members.length)
      : 0;
    const xp = members.reduce((s, m) => s + (m.xp || 0), 0);

    const counts = members.reduce(
      (acc, m) => {
        const s = getHealthStatus(m.last_active_date);
        acc[s]++;
        return acc;
      },
      { active: 0, 'at-risk': 0, inactive: 0 } as Record<HealthStatus, number>,
    );

    return {
      activeThisWeek: active7,
      activeLastWeek: active7to14,
      avgGameIQ: avg,
      totalXP: xp,
      healthCounts: counts,
    };
  }, [members]);


  // Real, computable trend only for "Active This Week" vs prior 7d window
  const activeDelta = activeThisWeek - activeLastWeek;

  const stats = [
    {
      label: 'Active This Week',
      value: `${activeThisWeek}`,
      suffix: ` / ${members.length}`,
      icon: Users,
      iconBg: 'bg-emerald-500/15 ring-emerald-400/30',
      iconColor: 'text-emerald-300',
      trend: members.length > 0 ? activeDelta : undefined,
      hint: members.length === 0 ? 'Add players to get started' : undefined,
    },
    {
      label: 'Avg Game IQ',
      value: `${avgGameIQ}`,
      icon: Trophy,
      iconBg: 'bg-violet-500/15 ring-violet-400/30',
      iconColor: 'text-violet-300',
      hint: avgGameIQ === 0 ? 'Increases as players complete lessons' : undefined,
    },
    {
      label: 'Total XP',
      value: totalXP.toLocaleString(),
      icon: Zap,
      iconBg: 'bg-primary/15 ring-primary/30',
      iconColor: 'text-primary',
      hint: totalXP === 0 ? "Your team's XP appears here" : undefined,
    },
    {
      label: 'Assignments Done',
      value: completionError ? '!' : completion.has_data ? `${completion.completion_pct}%` : '—',
      icon: ClipboardCheck,
      iconBg: 'bg-orange-500/15 ring-orange-400/30',
      iconColor: 'text-orange-300',
      hint: completionError
        ? 'Assignment progress could not be loaded'
        : completion.total_active_assignments === 0
        ? 'Assign a lesson to track completion'
        : completion.player_count === 0
          ? 'Add players to track completion'
          : `${completion.completed_assignment_instances}/${completion.total_assignment_instances} assignments completed`,
    },

  ] as const;

  const healthPills: { key: HealthStatus; emoji: string; label: string; count: number; ring: string; bg: string; text: string }[] = [
    { key: 'active',    emoji: '🟢', label: 'Active',     count: healthCounts.active,      ring: 'ring-emerald-400/40', bg: 'bg-emerald-500/10 hover:bg-emerald-500/20', text: 'text-emerald-300' },
    { key: 'at-risk',   emoji: '🟡', label: 'At Risk',    count: healthCounts['at-risk'],  ring: 'ring-amber-400/40',   bg: 'bg-amber-500/10 hover:bg-amber-500/20',     text: 'text-amber-300' },
    { key: 'inactive',  emoji: '🔴', label: 'Inactive',   count: healthCounts.inactive,    ring: 'ring-rose-400/40',    bg: 'bg-rose-500/10 hover:bg-rose-500/20',       text: 'text-rose-300' },
  ];

  return (
    <div className="space-y-3 mb-4">
      {/* 2x2 stats grid */}
      <div className="grid grid-cols-2 gap-3">
        {stats.map(stat => (
          <div
            key={stat.label}
            className="glass-card p-4 flex items-start gap-3 shadow-elevated"
          >
            <div className={`w-10 h-10 rounded-xl ring-1 flex items-center justify-center shrink-0 ${stat.iconBg}`}>
              <stat.icon className={`w-5 h-5 ${stat.iconColor}`} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-1.5">
                <p className="font-display font-extrabold text-2xl text-foreground leading-none truncate">
                  {stat.value}
                  {'suffix' in stat && stat.suffix && (
                    <span className="text-sm font-display font-bold text-muted-foreground">{stat.suffix}</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-1 mt-1">
                <p className="text-[11px] text-muted-foreground font-body truncate">{stat.label}</p>
                {'trend' in stat && stat.trend !== undefined && (
                  stat.trend > 0 ? (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-display font-bold text-emerald-300">
                      <TrendingUp className="w-3 h-3" />+{stat.trend}
                    </span>
                  ) : stat.trend < 0 ? (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-display font-bold text-rose-300">
                      <TrendingDown className="w-3 h-3" />{stat.trend}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-display font-bold text-muted-foreground">
                      <Minus className="w-3 h-3" />
                    </span>
                  )
                )}
              </div>
              {stat.hint && (
                <p className="text-[10px] text-muted-foreground/70 font-body mt-1 leading-tight">
                  {stat.hint}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Health traffic-light pills */}
      <div className="flex items-center gap-2">
        {healthPills.map(p => {
          const isActive = healthFilter === p.key;
          return (
            <button
              key={p.key}
              onClick={() => onHealthFilterChange(isActive ? null : p.key)}
              aria-pressed={isActive}
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-full px-3 py-2 ring-1 transition-all ${p.bg} ${p.ring} ${isActive ? 'shadow-glow-primary scale-[1.02]' : ''}`}
            >
              <span className="text-xs leading-none" aria-hidden>{p.emoji}</span>
              <span className={`text-xs font-display font-bold ${p.text}`}>{p.count}</span>
              <span className="text-[11px] font-body text-foreground/70 truncate">{p.label}</span>
            </button>
          );
        })}
      </div>
      {healthFilter && (
        <button
          onClick={() => onHealthFilterChange(null)}
          className="text-[11px] font-body text-muted-foreground hover:text-foreground underline underline-offset-2"
        >
          Clear filter
        </button>
      )}
    </div>
  );
}
